import { useModal } from "@ebay/nice-modal-react";
import { useEffect, useMemo } from "react";
import { Controller, FormProvider, useForm } from "react-hook-form";

import FlashContext from "components/FlashMessages/FlashContext";
import FormFieldSpace from "components/FormFields/Space";
import FormFieldTags from "components/FormFields/Tags";
import Box from "ds/components/Box";
import Button from "ds/components/Button";
import DrawerBody from "ds/components/Drawer/Body";
import DrawerFooter from "ds/components/Drawer/Footer";
import DrawerFooterActions from "ds/components/Drawer/FooterActions";
import DrawerHeader from "ds/components/Drawer/Header";
import DrawerCancelButton from "ds/components/DrawerNew/CancelButton";
import DrawerCloseIcon from "ds/components/DrawerNew/CloseIcon";
import DrawerForm from "ds/components/DrawerNew/Form";
import DrawerHeaderTitle from "ds/components/DrawerNew/HeaderTitle";
import { createDrawer, createDrawerTrigger } from "ds/components/DrawerNew/utils";
import FormField from "ds/components/Form/Field";
import Input from "ds/components/Input";
import Select from "ds/components/Select";
import SelectItem from "ds/components/Select/Item";
import Textarea from "ds/components/Textarea";
import useTypedContext from "hooks/useTypedContext";
import { TrackAnalyticsEventProperties } from "shared/Analytics";
import useAvailableAiModels from "shared/AiIntegration/useAvailableAiModels";
import useCreateAiIntegration from "shared/AiIntegration/useCreateAiIntegration";
import useUpdateAiIntegration from "shared/AiIntegration/useUpdateAiIntegration";
import { getDrawerFormFix } from "utils/css";
import { SpacesContext } from "views/Account/SpacesProvider";
import { DEFAULT_SPACE_NAME } from "views/constants";

import { AiIntegrationView } from "../../useSearchAiIntegrations/gql";

type FormValues = {
  name: string;
  description: string;
  labels: { value: string }[];
  provider: string;
  model: string;
  apiKey: string;
  space: string;
};

type AiIntegrationFormDrawerProps = {
  trackAnalytics: (event: string, props?: TrackAnalyticsEventProperties) => void;
  integration?: AiIntegrationView;
  refetchQueriesOnEdit?: string[];
  onCreated?: () => void;
};

const AiIntegrationFormDrawer = createDrawer(
  ({ integration, trackAnalytics, refetchQueriesOnEdit, onCreated }: AiIntegrationFormDrawerProps) => {
    const drawer = useModal();
    const isEditMode = !!integration?.id;

    const { onError, reportSuccess } = useTypedContext(FlashContext);
    const { manageableSpaces } = useTypedContext(SpacesContext);

    const builderForm = useForm<FormValues>({
      defaultValues: {
        name: integration?.name || "",
        description: integration?.description || "",
        labels: integration?.labels?.map((label) => ({ value: label })) || [],
        space:
          integration?.space?.id ||
          manageableSpaces.find((s) => s.id === DEFAULT_SPACE_NAME)?.id ||
          manageableSpaces[0]?.id,
        provider: integration?.provider,
        model: integration?.model,
        apiKey: "",
      },
      mode: "onChange",
    });

    const {
      control,
      register,
      handleSubmit,
      watch,
      setValue,
      formState: { errors, isDirty },
    } = builderForm;

    const selectedProvider = watch("provider");
    const selectedModel = watch("model");

    const { allProviderModels, loading: modelsLoading } = useAvailableAiModels();

    const providerOptions = allProviderModels.map((pm) => ({
      value: pm.provider,
      label: pm.provider,
    }));

    const availableModels = useMemo(
      () => allProviderModels.find((pm) => pm.provider === selectedProvider)?.models || [],
      [allProviderModels, selectedProvider]
    );

    const modelOptions = availableModels.map((model: string) => ({
      value: model,
      label: model,
    }));

    useEffect(() => {
      if (isEditMode || !availableModels.length) return;

      const isModelValid = availableModels.includes(selectedModel);
      if (!selectedModel || !isModelValid) {
        setValue("model", availableModels[0]);
      }
    }, [availableModels, selectedModel, isEditMode, setValue]);

    const [createIntegration, { loading: creatingIntegration }] = useCreateAiIntegration();

    const [updateIntegration, { loading: updatingIntegration }] = useUpdateAiIntegration({
      refetchQueries: refetchQueriesOnEdit,
    });

    useEffect(() => {
      trackAnalytics("Open Integration Form", {
        type: "ai",
        mode: isEditMode ? "edit" : "create",
      });
    }, [integration, isEditMode, trackAnalytics]);

    const handleCloseDrawer = () => {
      drawer.resolve();
      drawer.hide();
    };

    const onCreateSubmit = (formData: FormValues) => {
      createIntegration({
        variables: {
          name: formData.name.trim(),
          description: formData.description.trim() || null,
          labels: formData.labels.map((label) => label.value),
          space: formData.space,
          provider: formData.provider,
          model: formData.model,
          apiKey: formData.apiKey,
        },
      })
        .then(({ data }) => {
          if (data?.aiIntegrationCreate?.id) {
            handleCloseDrawer();
            reportSuccess({ message: "AI integration was successfully created" });
            trackAnalytics("Create Integration", { type: "ai" });
            onCreated?.();
          }
        })
        .catch(onError);
    };

    const onUpdateSubmit = (formData: FormValues) => {
      if (!integration) {
        return;
      }

      updateIntegration({
        variables: {
          id: integration.id,
          name: formData.name.trim(),
          description: formData.description.trim() || null,
          labels: formData.labels.map((label) => label.value),
          space: formData.space,
          provider: formData.provider,
          model: formData.model,
          apiKey: formData.apiKey || null,
        },
      })
        .then(({ data }) => {
          if (data?.aiIntegrationUpdate?.id) {
            reportSuccess({ message: "AI integration was successfully updated" });
            handleCloseDrawer();
            trackAnalytics("Update Integration", { type: "ai" });
          }
        })
        .catch(onError);
    };

    const onSubmit = (formData: FormValues) => {
      if (isEditMode) {
        onUpdateSubmit(formData);
      } else {
        onCreateSubmit(formData);
      }
    };

    const onClose = () => {
      trackAnalytics("Close Integration Form", {
        type: "ai",
        mode: isEditMode ? "edit" : "create",
        dirty: isDirty,
      });
    };

    return (
      <DrawerForm isDirty={isDirty} onClose={onClose}>
        <FormProvider {...builderForm}>
          <form onSubmit={handleSubmit(onSubmit)} {...getDrawerFormFix()}>
            <DrawerHeader justify="between">
              <DrawerHeaderTitle
                title={isEditMode ? "Edit AI integration" : "Set up AI integration"}
              />
              <DrawerCloseIcon />
            </DrawerHeader>

            <DrawerBody gap="x-large" fullHeight hasStickyFooter>
              <FormField label="Name" error={errors?.name?.message} noMargin>
                {({ ariaInputProps }) => (
                  <Input
                    placeholder="Name your integration"
                    error={!!errors?.name}
                    {...register("name", { required: "Name field is required." })}
                    {...ariaInputProps}
                  />
                )}
              </FormField>

              <FormField label="Description" error={errors?.description?.message} isOptional noMargin>
                {({ ariaInputProps }) => (
                  <Textarea
                    placeholder="Describe your integration"
                    error={!!errors?.description}
                    {...register("description")}
                    {...ariaInputProps}
                  />
                )}
              </FormField>

              <FormFieldSpace noMargin />

              <Controller
                name="provider"
                control={control}
                rules={{ required: "Provider is required." }}
                render={({ field }) => (
                  <Select
                    label="Provider"
                    error={errors?.provider?.message}
                    placeholder="Select a provider"
                    value={field.value}
                    onChange={(value) => field.onChange(value)}
                    items={providerOptions}
                    isDisabled={isEditMode}
                  >
                    {(item) => <SelectItem {...item} id={item.value} />}
                  </Select>
                )}
              />

              <Controller
                name="model"
                control={control}
                rules={{ required: "Model is required." }}
                render={({ field }) => (
                  <Select
                    label="Model"
                    error={errors?.model?.message}
                    placeholder={modelsLoading ? "Loading models..." : "Select a model"}
                    value={field.value}
                    onChange={(value) => field.onChange(value)}
                    items={modelOptions}
                    isDisabled={modelsLoading || modelOptions.length === 0}
                  >
                    {(item) => <SelectItem {...item} id={item.value} />}
                  </Select>
                )}
              />

              <FormField
                label="API Key"
                error={errors?.apiKey?.message}
                isOptional={isEditMode}
                noMargin
              >
                {({ ariaInputProps }) => (
                  <Input
                    type="password"
                    placeholder={isEditMode ? "Leave empty to keep existing key" : "Enter your API key"}
                    error={!!errors?.apiKey}
                    {...register("apiKey", {
                      required: isEditMode ? false : "API Key is required."
                    })}
                    {...ariaInputProps}
                  />
                )}
              </FormField>

              <Box direction="column">
                <FormFieldTags
                  label="Labels"
                  tagName="label"
                  name="labels"
                  isOptional
                  noMargin
                />
              </Box>

              <DrawerFooter sticky>
                <DrawerFooterActions>
                  <DrawerCancelButton />
                  <Button
                    variant="primary"
                    type="submit"
                    disabled={creatingIntegration || updatingIntegration}
                    loading={creatingIntegration || updatingIntegration}
                  >
                    {isEditMode ? "Save" : "Set up"}
                  </Button>
                </DrawerFooterActions>
              </DrawerFooter>
            </DrawerBody>
          </form>
        </FormProvider>
      </DrawerForm>
    );
  }
);

export const showAiIntegrationFormDrawer = createDrawerTrigger(AiIntegrationFormDrawer);
